export type ContractWizardFields = Record<string, string>;

const methods = new Set(["PIX", "TRANSFERENCIA", "DINHEIRO", "BOLETO", "OUTRO"]);
const periods = new Set(["MENSAL", "QUINZENAL", "SEMANAL"]);

export function parsePtBrMoneyToCents(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? Math.round(value * 100) : null;
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  let normalized = raw.replace(/R\$/gi, "").replace(/[\s\u00a0]/g, "");
  if (!/^-?[\d.,]+$/.test(normalized)) return null;
  if (normalized.includes(",")) normalized = normalized.replace(/\./g, "").replace(",", ".");
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalized)) normalized = normalized.replace(/\./g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

export function validIsoDate(value: unknown) {
  const text = String(value ?? "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const [year, month, day] = text.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export function validateContractWizardForm(form: Record<string, any>): ContractWizardFields {
  const fields: ContractWizardFields = {};
  const requiredMoney = (key: string, value: unknown, message: string) => {
    const cents = parsePtBrMoneyToCents(value);
    if (cents === null || cents <= 0) fields[key] = message;
    return cents ?? 0;
  };
  const optionalMoney = (key: string, value: unknown, message: string) => {
    if (String(value ?? "").trim() && parsePtBrMoneyToCents(value) === null) fields[key] = message;
  };
  if (form.existingClient ? !form.clientId : String(form.clientName || "").trim().length < 2) fields[form.existingClient ? "clientId" : "clientName"] = "Selecione um cliente ou informe o nome do novo cliente.";
  if (!form.modality) fields.modality = "Selecione a modalidade do contrato.";
  if (String(form.city || "").trim().length < 2) fields.city = "Informe a cidade do contrato.";
  const constructionCents = requiredMoney("construction", form.construction, "Informe o valor contratual da construção.");
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (form.clientEmail && !emailPattern.test(String(form.clientEmail).trim())) fields.clientEmail = "Informe um e-mail válido para o cliente.";
  optionalMoney("pciConstruction", form.pciConstruction, "O valor da construção na PCI é inválido.");
  for (const [key, label] of [["approved", "O valor aprovado é inválido."], ["subsidy", "O valor do subsídio é inválido."], ["fgts", "O valor do FGTS é inválido."], ["ownResources", "O valor dos recursos próprios é inválido."], ["land", "O valor do terreno é inválido."]] as const) optionalMoney(key, form[key], label);
  const pciCents = parsePtBrMoneyToCents(form.pciConstruction) ?? 0;
  if (pciCents > 0 && pciCents !== constructionCents && String(form.pciDifferenceJustification || "").trim().length < 5) fields.pciDifferenceJustification = "Justifique a diferença entre o contrato e a PCI.";
  for (const [key, label] of [["signatureDate", "Data da assinatura"], ["startDate", "Data prevista de início"], ["expectedEndDate", "Data prevista de término"], ["pciDate", "Data da PCI"]] as const) {
    if (form[key] && !validIsoDate(form[key])) fields[key] = `${label} inválida.`;
  }
  if (validIsoDate(form.startDate) && validIsoDate(form.expectedEndDate) && form.expectedEndDate <= form.startDate) fields.expectedEndDate = "A data prevista de término deve ser posterior à data de início.";
  if (form.brokerMode === "EXISTING" && !form.brokerId) fields.brokerId = "Selecione o corretor.";
  if (form.brokerMode === "NEW" && String(form.brokerName || "").trim().length < 2) fields.brokerName = "Informe o nome do corretor.";
  if (form.brokerMode === "NEW" && form.brokerEmail && !emailPattern.test(String(form.brokerEmail).trim())) fields.brokerEmail = "Informe um e-mail válido para o corretor.";
  if (form.brokerMode !== "NONE" && form.remunerationType === "PERCENTUAL" && !(Number(String(form.commissionPercent || "").replace(",", ".")) > 0)) fields.commissionPercent = "Informe o percentual da comissão.";
  if (form.brokerMode !== "NONE" && form.remunerationType === "FIXO") requiredMoney("commissionFixed", form.commissionFixed, "Informe o valor fixo da comissão.");
  if (form.hasEntry) {
    const entryCents = requiredMoney("entryTotal", form.entryTotal, "Informe o valor total da entrada.");
    if (entryCents > constructionCents) fields.entryTotal = "A entrada não pode superar a receita da construtora neste contrato.";
    const signalCents = parsePtBrMoneyToCents(form.entrySignal);
    if (signalCents === null || signalCents < 0) fields.entrySignal = "O valor do sinal é inválido.";
    else if (signalCents > entryCents) fields.entrySignal = "O valor do sinal não pode ser maior que a entrada.";
    const balance = entryCents - Math.max(0, signalCents ?? 0);
    if ((signalCents ?? 0) > 0 && !methods.has(form.signalMethod)) fields.signalMethod = "Selecione a forma de recebimento do sinal.";
    if ((signalCents ?? 0) > 0 && form.signalReceived) {
      if (!validIsoDate(form.signalReceivedAt)) fields.signalReceivedAt = "Informe uma data efetiva válida para o sinal.";
      if (!form.signalBankAccountId) fields.signalBankAccountId = "Selecione a conta financeira do sinal recebido.";
    } else if ((signalCents ?? 0) > 0 && !validIsoDate(form.signalDueDate)) fields.signalDueDate = "Informe um vencimento válido para o sinal.";
    if (balance > 0) {
      const quantity = Number(form.entryInstallments);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 360) fields.entryInstallments = "Informe a quantidade de parcelas entre 1 e 360.";
      if (!validIsoDate(form.entryFirstDueDate)) fields.entryFirstDueDate = "Informe o primeiro vencimento.";
      if (!periods.has(form.entryPeriodicity)) fields.entryPeriodicity = "Selecione a periodicidade.";
      if (!methods.has(form.entryMethod)) fields.entryMethod = "Selecione a forma de recebimento das parcelas.";
      if (Number.isInteger(quantity) && quantity > 0) {
        const regular = Math.floor(balance / quantity);
        const total = regular * Math.max(0, quantity - 1) + (balance - regular * Math.max(0, quantity - 1));
        if (total !== entryCents - (signalCents ?? 0)) fields.entryInstallments = "A soma do sinal e das parcelas não corresponde à entrada total.";
      }
    }
  }
  return fields;
}

export function wizardStepForField(field: string) {
  return getContractWizardField(field).step;
}

export function safeContractDraft(form: Record<string, any>) {
  return { ...form, clientDocument: "", brokerDocument: "", signalBankAccountId: "" };
}
import { getContractWizardField } from "../../shared/contract-wizard-fields";
